import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { useCadastrarCliente, useEditarCliente } from '../../shared/api/queries.ts';
import type { Cliente } from '../../shared/api/tipos.ts';
import { ApiError } from '../../shared/api/client.ts';
import { CampoEmails } from '../../shared/ui/CampoEmails.tsx';
import { SecaoCertificado } from './SecaoCertificado.tsx';
import { mascararCnpj, somenteDigitos } from '../../shared/format.ts';

const VAZIO = { razaoSocial: '', cnpj: '', ativa: true, emails: [] as string[] };

/**
 * Cadastro e edição no mesmo diálogo: os campos são os mesmos e as regras
 * também, então duas telas quase idênticas só criariam duas chances de
 * divergir. `cliente` indefinido significa cadastro novo.
 */
export function DialogoCliente({
  aberto,
  cliente,
  aoFechar,
}: {
  aberto: boolean;
  cliente?: Cliente | undefined;
  aoFechar: () => void;
}) {
  const editando = cliente !== undefined;

  const [campos, setCampos] = useState(VAZIO);
  const cadastrar = useCadastrarCliente();
  const editar = useEditarCliente();
  const operacao = editando ? editar : cadastrar;

  // Ao abrir, carrega os dados de quem está sendo editado — ou limpa, no caso
  // de um cadastro novo.
  useEffect(() => {
    if (!aberto) return;
    setCampos(
      cliente === undefined
        ? VAZIO
        : {
            razaoSocial: cliente.razaoSocial,
            cnpj: mascararCnpj(cliente.cnpj),
            ativa: cliente.ativa,
            emails: [...cliente.emails],
          },
    );
    cadastrar.reset();
    editar.reset();
    // Rodar só na abertura e na troca de cliente é a intenção: incluir as
    // mutações nas dependências limparia o erro assim que ele aparecesse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, cliente]);

  const cnpjCompleto = somenteDigitos(campos.cnpj).length === 14;
  const razaoSocialOk = campos.razaoSocial.trim().length >= 3;
  const podeEnviar = cnpjCompleto && razaoSocialOk && !operacao.isPending;

  function fechar() {
    setCampos(VAZIO);
    cadastrar.reset();
    editar.reset();
    aoFechar();
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    const dados = {
      razaoSocial: campos.razaoSocial.trim(),
      cnpj: somenteDigitos(campos.cnpj),
      emails: campos.emails,
    };

    if (cliente === undefined) {
      cadastrar.mutate(dados, { onSuccess: fechar });
    } else {
      editar.mutate({ id: cliente.id, ...dados, ativa: campos.ativa }, { onSuccess: fechar });
    }
  }

  return (
    <Dialog
      open={aberto}
      onClose={operacao.isPending ? undefined : fechar}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: enviar } }}
    >
      <DialogTitle>{editando ? 'Editar cliente' : 'Novo cliente'}</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {operacao.isError && <Alert severity="error">{mensagemDoErro(operacao.error)}</Alert>}

          <TextField
            label="Razão social"
            value={campos.razaoSocial}
            // Em caixa alta já na digitação, e não só por CSS: o que se vê na
            // tela é exatamente o que vai ser gravado.
            onChange={(evento) =>
              setCampos((atual) => ({
                ...atual,
                razaoSocial: evento.target.value.toUpperCase(),
              }))
            }
            required
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { maxLength: 200, autoCapitalize: 'characters' } }}
          />

          <TextField
            label="CNPJ"
            value={campos.cnpj}
            onChange={(evento) =>
              setCampos((atual) => ({ ...atual, cnpj: mascararCnpj(evento.target.value) }))
            }
            required
            fullWidth
            placeholder="00.000.000/0000-00"
            // O formato é checado aqui para dar resposta imediata; os dígitos
            // verificadores e a unicidade ficam com o servidor, que é onde as
            // regras moram.
            error={campos.cnpj !== '' && !cnpjCompleto}
            helperText={
              campos.cnpj !== '' && !cnpjCompleto ? 'O CNPJ precisa de 14 dígitos.' : ' '
            }
            slotProps={{
              htmlInput: {
                maxLength: 18,
                inputMode: 'numeric',
                style: { fontVariantNumeric: 'tabular-nums' },
              },
            }}
          />

          <CampoEmails
            emails={campos.emails}
            aoMudar={(emails) => setCampos((atual) => ({ ...atual, emails }))}
          />

          {/* Só na edição: um cliente nasce ativo, e oferecer a opção no
              cadastro seria uma pergunta sem resposta útil. */}
          {editando && (
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={campos.ativa}
                    onChange={(evento) =>
                      setCampos((atual) => ({ ...atual, ativa: evento.target.checked }))
                    }
                  />
                }
                label="Cliente ativo"
              />
              <FormHelperText sx={{ mt: 0 }}>
                Desmarcar inativa o cliente. Nada é apagado: os documentos já escriturados
                continuam, e o CNPJ segue reservado para ele.
              </FormHelperText>
            </div>
          )}

          {/* O certificado vale sozinho: ele sobe na hora, e não com o botão
              Salvar. Por isso a seção fica por último, depois do que é
              formulário. */}
          <SecaoCertificado cliente={cliente} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={fechar} disabled={operacao.isPending}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!podeEnviar}
          loading={operacao.isPending}
        >
          {editando ? 'Salvar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    return erro.codigo === 'DOMINIO_CONFLITO'
      ? 'Já existe um cliente cadastrado com este CNPJ.'
      : erro.message;
  }
  return erro instanceof Error ? erro.message : 'Não foi possível salvar o cliente.';
}
